import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

export const generatePdf = (formData, results) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Helper function for percentage formatting
  const formatPercentage = (percentage) => {
    return `${percentage.toFixed(1)}%`;
  };
  
  // Add branding header
  doc.setFillColor(41, 128, 185); // Brand blue color
  doc.rect(0, 0, pageWidth, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.text('Package Promote Scale', 14, 10);
  doc.setFontSize(8);
  doc.text('jodie@packagepromotescale.com', pageWidth - 14, 10, { align: 'right' });
  
  // Title
  doc.setFontSize(20);
  doc.setTextColor(44, 62, 80);
  doc.text('Profitability Calculation Report', pageWidth / 2, 30, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text('Generated on ' + new Date().toLocaleDateString('en-AU'), pageWidth / 2, 37, { align: 'center' });
  
  // Input Parameters Section
  doc.setFontSize(16);
  doc.setTextColor(44, 62, 80);
  doc.text('Input Parameters', 14, 50);
  
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  
  // Fixed Costs
  doc.setFontSize(14);
  doc.setTextColor(41, 128, 185);
  doc.text('Fixed Costs', 14, 60);
  
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text('Employee Costs: ' + formatCurrency(parseFloat(formData.employeeCosts) || 0), 20, 67);
  doc.text('Materials: ' + formatCurrency(parseFloat(formData.materials) || 0), 20, 74);
  doc.text('Subcontractors: ' + formatCurrency(parseFloat(formData.subcontractors) || 0), 20, 81);
  doc.text('Other Fixed Costs: ' + formatCurrency(parseFloat(formData.otherFixedCosts) || 0), 20, 88);
  
  // Variable Costs
  doc.setFontSize(14);
  doc.setTextColor(41, 128, 185);
  doc.text('Variable Costs', 14, 98);
  
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text('Consultant Hours: ' + (parseFloat(formData.consultantHours) || 0) + ' hrs', 20, 105);
  doc.text('Hourly Rate: ' + formatCurrency(parseFloat(formData.hourlyRate) || 0), 20, 112);
  doc.text('Project Admin: ' + formatCurrency(parseFloat(formData.projectAdmin) || 0), 20, 119);
  doc.text('Other Variable Costs: ' + formatCurrency(parseFloat(formData.otherVariableCosts) || 0), 20, 126);
  
  // Package Details
  doc.setFontSize(14);
  doc.setTextColor(41, 128, 185);
  doc.text('Package Details', 14, 136);
  
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text('Package Price (inc. GST): ' + formatCurrency(parseFloat(formData.packagePrice) || 0), 20, 143);
  
  if (formData.referralFeeValue && parseFloat(formData.referralFeeValue) > 0) {
    const referralFeeType = formData.referralFeeType === 'percentage' ? '%' : '$';
    const referralFeeDisplay = formData.referralFeeType === 'percentage' 
      ? `${formData.referralFeeValue}%`
      : formatCurrency(parseFloat(formData.referralFeeValue) || 0);
    doc.text(`Referral Fee (${referralFeeType}): ${referralFeeDisplay}`, 20, 150);
  }
  
  doc.text('Contingency Buffer: ' + formData.contingencyBuffer + '%', 20, 157);
  doc.text('Income Tax Rate: ' + formData.incomeTaxRate + '%', 20, 164);
  
  // Results Section
  doc.addPage();
  
  // Add branding header to second page
  doc.setFillColor(41, 128, 185);
  doc.rect(0, 0, pageWidth, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.text('Package Promote Scale', 14, 10);
  doc.setFontSize(8);
  doc.text('jodie@packagepromotescale.com', pageWidth - 14, 10, { align: 'right' });
  
  doc.setFontSize(16);
  doc.setTextColor(44, 62, 80);
  doc.text('Calculation Results', 14, 30);
  
  // Create results table
  const resultData = [
    ['Package Price (inc. GST)', formatCurrency(results.adjustedPackagePrice + results.referralFee)],
  ];
  
  // Only add referral fee if there is one
  if (results.referralFee > 0) {
    resultData.push(['Referral Fee', '- ' + formatCurrency(results.referralFee)]);
    resultData.push(['Adjusted Package Price', formatCurrency(results.adjustedPackagePrice)]);
  }
  
  resultData.push(
    ['GST Component', '- ' + formatCurrency(results.gst)],
    ['Net Revenue', formatCurrency(results.netRevenue)],
    ['Total Delivery Costs', '- ' + formatCurrency(results.totalDeliveryCosts)],
    ['Gross Profit', formatCurrency(results.grossProfit)],
    ['Estimated Tax', '- ' + formatCurrency(results.estimatedTax)],
    ['After-Tax Profit', formatCurrency(results.afterTaxProfit)],
    ['Final Margin', formatPercentage(results.margin)]
  );
  
  doc.autoTable({
    startY: 40,
    head: [['Item', 'Value']],
    body: resultData,
    theme: 'grid',
    headStyles: { fillColor: [41, 128, 185], textColor: 255 },
    alternateRowStyles: { fillColor: [240, 248, 255] },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 60, halign: 'right' }
    }
  });
  
  // Margin Assessment
  const getMarginMessage = (margin) => {
    if (margin >= 60) {
      return "This result suggests the package is highly profitable. It leaves strong room for reinvestment, sustainability, and business growth.";
    } else if (margin >= 50) {
      return "This package appears well-priced and profitable. It should support both delivery and long-term viability.";
    } else if (margin >= 40) {
      return "This may be viable in the short term, but your margin is lean. Consider reviewing pricing or cost assumptions.";
    } else {
      return "This margin is likely too low to sustain a healthy business. Consider adjusting price, scope, or delivery methods.";
    }
  };
  
  const marginMessage = getMarginMessage(results.margin);
  
  doc.setFontSize(14);
  doc.setTextColor(41, 128, 185);
  doc.text('Margin Assessment', 14, doc.autoTable.previous.finalY + 20);
  
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  
  const splitMarginText = doc.splitTextToSize(marginMessage, pageWidth - 40);
  doc.text(splitMarginText, 14, doc.autoTable.previous.finalY + 30);
  
  // Disclaimer
  const disclaimer = "DISCLAIMER: This tool provides general information only and does not constitute financial or legal advice. Results are estimates only. Please consult a registered accountant or commercial lawyer for personalised advice.";
  
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  
  const splitDisclaimer = doc.splitTextToSize(disclaimer, pageWidth - 40);
  doc.text(splitDisclaimer, 14, doc.internal.pageSize.getHeight() - 20);
  
  // Add footer with contact
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text('Prepared by Jodie Willmer | Package Promote Scale', pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
  
  return doc;
};